import type { ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Search, ShieldCheck } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  exceptionSearchChanged,
  fetchExceptions,
  selectVisibleExceptions
} from '@/features/exceptions/exceptionsSlice'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, EmptyState, Spinner } from '@/components/ui/Surface'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/Field'
import { formatDateTime, formatRelative } from '@/lib/format'

export function ExceptionsPage(): ReactNode {
  const dispatch = useAppDispatch()
  const rows = useAppSelector(selectVisibleExceptions)
  const { items, loading, error, search } = useAppSelector((s) => s.exceptions)

  return (
    <>
      <PageHeader
        title="Exceptions"
        description="Errors the desktop clients reported back to the cloud."
        actions={
          <Button
            variant="secondary"
            icon={RefreshCw}
            loading={loading}
            onClick={() => void dispatch(fetchExceptions())}
          >
            Refresh
          </Button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <div className="min-w-[220px] flex-1">
            <TextInput
              icon={Search}
              value={search}
              onChange={(e) => dispatch(exceptionSearchChanged(e.target.value))}
              placeholder="Search dealer email or message…"
              aria-label="Search exceptions"
            />
          </div>
          <span className="label-caps ml-auto whitespace-nowrap px-1 text-subtle">
            {rows.length} / {items.length}
          </span>
        </div>

        {error ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-negative/10 text-negative ring-1 ring-inset ring-negative/30">
              <AlertTriangle size={18} aria-hidden />
            </span>
            <p className="text-[13px] font-semibold text-fg">Could not load exceptions</p>
            <p className="max-w-sm text-[12px] text-muted">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              onClick={() => void dispatch(fetchExceptions())}
            >
              Try again
            </Button>
          </div>
        ) : loading && items.length === 0 ? (
          <Spinner label="Loading exceptions" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title={items.length === 0 ? 'No exceptions reported' : 'Nothing matches that search'}
            description={
              items.length === 0
                ? 'Client machines have not sent any errors to the cloud.'
                : 'Try a different dealer email or message fragment.'
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((e) => (
              <li key={e.id} className="px-5 py-3.5">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="text-[12.5px] font-medium text-fg">
                    {e.user_email || 'unknown dealer'}
                  </p>
                  <p className="text-[10.5px] text-subtle" title={formatDateTime(e.created_at)}>
                    {formatRelative(e.created_at)} · {formatDateTime(e.created_at)}
                  </p>
                </div>
                <pre className="scroll-slim mt-1.5 overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-line bg-raised px-3 py-2 font-mono text-[11px] leading-relaxed text-muted">
                  {e.exception}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  )
}
